import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Tag } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

const PACKAGES = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 199,
    annualPricePerMonth: 149,
    words: '15,000',
    desc: 'Perfect for short books, guides, and novellas',
    features: ['Up to 15,000 words/month', '2 rounds of revisions', 'Professional formatting', 'Digital delivery (PDF + DOCX)'],
    popular: false
  },
  {
    id: 'professional',
    name: 'Professional',
    monthlyPrice: 395,
    annualPricePerMonth: 296,
    words: '50,000',
    desc: 'Ideal for full-length novels and non-fiction books',
    features: ['Up to 50,000 words/month', '5 rounds of revisions', 'Professional formatting', 'Cover design consultation', 'Amazon KDP formatting'],
    popular: true
  },
  {
    id: 'complete',
    name: 'Complete',
    monthlyPrice: 799,
    annualPricePerMonth: 599,
    words: 'Unlimited',
    desc: 'The ultimate publishing package for serious authors',
    features: ['Unlimited words/month', 'Unlimited revisions', 'Professional formatting', 'Custom book cover design', 'Amazon KDP + IngramSpark', 'Marketing strategy guide'],
    popular: false
  }
];

export default function GhostwriterPricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const questionnaireId = searchParams.get('q');
  const preselectedPackage = searchParams.get('package');
  const preselectedBilling = searchParams.get('billing');
  const [billingCycle, setBillingCycle] = useState(preselectedBilling || 'annual');
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [loading, setLoading] = useState(null);

  const isAnnual = billingCycle === 'annual';

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    try {
      const { data } = await api.get(`/promo/validate/${promoCode.trim()}`);
      setPromoResult(data);
    } catch { setPromoResult({ valid: false, message: 'Invalid code' }); }
  };

  const getPrice = (pkg) => {
    return isAnnual ? pkg.annualPricePerMonth : pkg.monthlyPrice;
  };

  const getDiscountedPrice = (price) => {
    if (!promoResult?.valid) return price;
    if (promoResult.type === 'flat') return Math.max(0, price - promoResult.value);
    if (promoResult.type === 'percent') return price * (1 - promoResult.value / 100);
    return price;
  };

  const handlePurchase = async (pkgId) => {
    // If no questionnaire yet, redirect to onboarding first
    if (!questionnaireId) {
      navigate(`/get-started?package=${pkgId}&billing=${billingCycle}`);
      return;
    }
    setLoading(pkgId);
    try {
      const { data } = await api.post('/ghostwriter/purchase', {
        package_id: pkgId,
        billing_cycle: billingCycle,
        promo_code: promoResult?.valid ? promoCode.trim() : null,
        origin_url: window.location.origin,
        questionnaire_id: questionnaireId,
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Error creating checkout');
    } finally { setLoading(null); }
  };

  const handlePayPalPurchase = async (pkgId) => {
    if (!questionnaireId) {
      navigate(`/get-started?package=${pkgId}&billing=${billingCycle}`);
      return;
    }
    setLoading(`paypal-${pkgId}`);
    try {
      const { data } = await api.post('/paypal/create-subscription', {
        package_id: pkgId,
        billing_cycle: billingCycle,
        promo_code: promoResult?.valid ? promoCode.trim() : null,
        origin_url: window.location.origin
      });
      if (data.approval_url) window.location.href = data.approval_url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Error creating PayPal subscription');
    } finally { setLoading(null); }
  };

  return (
    <>
      <SeoHead
        title="Ghostwriting Pricing Plans — Affordable Book Ghostwriter Packages | AI Ghostwriter Pro"
        description="Compare ghostwriting subscription plans from $149/mo. Starter (15,000 words/month), Professional (50,000 words/month), and Complete (unlimited words). 7-day free trial, 25% off annual plans. Hire a professional ghostwriter today."
        keywords="ghostwriting cost, ghostwriter pricing, how much does a ghostwriter cost, affordable ghostwriting service, ghostwriter rates per word, book ghostwriting packages, cheap ghostwriter, ghostwriting fees, ghostwriter prices"
        path="/pricing"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "AI Ghostwriter Pro — Book Ghostwriting Service",
            "description": "Professional AI-powered book ghostwriting with flexible monthly and annual subscription plans",
            "url": "https://aighostwriterpro.com/pricing",
            "brand": {"@type": "Brand", "name": "AI Ghostwriter Pro"},
            "offers": [
              {"@type": "Offer", "name": "Starter Plan", "price": "149", "priceCurrency": "USD", "priceSpecification": {"@type": "UnitPriceSpecification", "billingDuration": "P1M"}, "description": "15,000 words/month, 2 revisions, professional formatting"},
              {"@type": "Offer", "name": "Professional Plan", "price": "296", "priceCurrency": "USD", "priceSpecification": {"@type": "UnitPriceSpecification", "billingDuration": "P1M"}, "description": "50,000 words/month, 5 revisions, cover consultation"},
              {"@type": "Offer", "name": "Complete Plan", "price": "599", "priceCurrency": "USD", "priceSpecification": {"@type": "UnitPriceSpecification", "billingDuration": "P1M"}, "description": "Unlimited words/month, unlimited revisions, full publishing support"}
            ]
          }
        ]}
      />
      
      <section className="pt-28 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Pricing</p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-4 md:mb-6">Choose Your <span className="text-primary">Plan</span></h1>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed text-sm md:text-base">Every plan includes full copyright ownership, professional formatting, and dedicated support.</p>
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mb-12 md:mb-16" data-testid="billing-toggle-container">
            <button
              onClick={() => setBillingCycle('monthly')}
              data-testid="toggle-monthly"
              className={`px-5 py-2.5 text-sm font-semibold rounded-l-sm border transition-all duration-300 ${
                !isAnnual
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-white/5 text-muted-foreground border-white/10 hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              data-testid="toggle-annual"
              className={`px-5 py-2.5 text-sm font-semibold rounded-r-sm border border-l-0 transition-all duration-300 relative ${
                isAnnual
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-white/5 text-muted-foreground border-white/10 hover:text-foreground'
              }`}
            >
              Annual
              <span className="absolute -top-3 -right-3 bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                -25%
              </span>
            </button>
          </div>

          {/* Promo Code */}
          <div className="max-w-md mx-auto mb-12 md:mb-16" data-testid="promo-section">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  data-testid="promo-input"
                  className="w-full pl-10 pr-4 h-12 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-1 focus:ring-primary/50 outline-none"
                />
              </div>
              <button onClick={validatePromo} data-testid="promo-apply-btn" className="px-6 h-12 rounded-sm border border-white/20 text-foreground font-semibold hover:border-primary hover:text-primary transition-all">
                Apply
              </button>
            </div>
            {promoResult && (
              <p className={`text-sm mt-2 ${promoResult.valid ? 'text-green-400' : 'text-red-400'}`} data-testid="promo-message">
                {promoResult.message}
              </p>
            )}
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {PACKAGES.map((pkg) => {
              const basePrice = getPrice(pkg);
              const discounted = getDiscountedPrice(basePrice);
              const hasDiscount = discounted < basePrice;
              return (
                <div
                  key={pkg.id}
                  data-testid={`pricing-card-${pkg.id}`}
                  className={`relative bg-white/5 border backdrop-blur-md rounded-sm p-8 transition-all duration-500 ${pkg.popular ? 'border-primary/50 scale-[1.02] shadow-[0_0_40px_-10px_rgba(212,175,55,0.3)]' : 'border-white/10 hover:border-primary/30'}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest px-4 py-1 rounded-sm">
                      Most Popular
                    </div>
                  )}
                  {isAnnual && (
                    <div className="absolute top-4 right-4 bg-green-500/20 text-green-400 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm" data-testid={`save-badge-${pkg.id}`}>
                      Save 25%
                    </div>
                  )}
                  <h3 className="font-serif text-2xl mb-2">{pkg.name}</h3>
                  <p className="text-sm text-muted-foreground mb-6">{pkg.desc}</p>
                  <div className="mb-6">
                    {hasDiscount && <p className="text-sm text-muted-foreground line-through">${basePrice}</p>}
                    <div className="flex items-baseline gap-1">
                      <p className="font-serif text-4xl text-primary" data-testid={`price-${pkg.id}`}>${Math.round(discounted)}</p>
                      <span className="text-sm text-muted-foreground">/mo</span>
                    </div>
                    {isAnnual && (
                      <p className="text-xs text-muted-foreground mt-1">
                        ${(pkg.annualPricePerMonth * 12).toLocaleString()}/yr — billed annually
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">{pkg.words === 'Unlimited' ? 'Unlimited words' : `Up to ${pkg.words} words/month`}</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="space-y-3">
                    <p className="text-xs text-center text-green-400 font-medium">7-day free trial — no credit card needed</p>
                    <button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={loading === pkg.id}
                      data-testid={`purchase-btn-${pkg.id}`}
                      className={`w-full rounded-sm py-3 font-semibold tracking-wide transition-all duration-300 flex items-center justify-center gap-2 ${pkg.popular
                        ? 'bg-primary text-primary-foreground hover:bg-white hover:text-black shadow-[0_0_15px_rgba(212,175,55,0.3)]'
                        : 'border border-white/20 text-foreground hover:border-primary hover:text-primary'}`}
                    >
                      {loading === pkg.id ? 'Processing...' : <>Subscribe with Card <ArrowRight className="w-4 h-4" /></>}
                    </button>
                    <button
                      onClick={() => handlePayPalPurchase(pkg.id)}
                      disabled={loading === `paypal-${pkg.id}`}
                      data-testid={`paypal-btn-${pkg.id}`}
                      className="w-full rounded-sm py-3 font-semibold tracking-wide transition-all duration-300 flex items-center justify-center gap-2 border border-white/10 text-muted-foreground hover:border-[#0070ba] hover:text-[#0070ba]"
                    >
                      {loading === `paypal-${pkg.id}` ? 'Processing...' : 'Subscribe with PayPal'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Money back */}
          <div className="text-center mt-16">
            <p className="text-sm text-primary font-semibold mb-2">Start with a 7-day free trial (7,500 words) — no credit card required.</p>
            <p className="text-sm text-muted-foreground">All plans include a satisfaction guarantee with multiple revision rounds. Cancel anytime.</p>
          </div>
        </div>
      </section>
    </>
  );
}
